import { useEffect, useRef, useState } from 'react'
import type {
    ClienteDTO,
    Conducto,
    KeyValue,
    PagaebleResult,
    SearchPolizaDTO,
    SearchVendedorDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    NEW_POLICY_PATH,
    pageWindow,
    personName,
    POLICIES_PATH,
    policyPath,
    statusView,
    vigenciaView,
} from '../common/policy-common'
import type { PolicyFormLocationState, StatusChip, VigenciaView } from '../common/policy-common'

export const PAGE_SIZES = [20, 50, 100]
export { RAMOS } from '../common/policy-common'
const TEXT_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de pólizas.'

const currentYear = new Date().getFullYear()
export const YEARS = Array.from({ length: 11 }, (_, index) => currentYear + 1 - index)

export interface PolicyListFilters {
    cliente: ClienteDTO | null
    vendedor: SearchVendedorDTO | null
    noPoliza: string
    concepto: string
    abreviacion: string
    anio: number | ''
    conducto: Conducto | ''
    ramo: string
    esRobot: boolean
}

export type PolicyTextFilter = 'noPoliza' | 'concepto' | 'abreviacion'

export interface PolicyRow {
    id: number
    noPoliza: string | null
    ramo: string | null
    esRobot: boolean
    name: string
    seller: string
    concepto: string | null
    aseguradora: string | null
    agenteClave: string | null
    total: number | null
    moneda: string | null
    status: StatusChip
    vigencia: VigenciaView
}

const defaultFilters: PolicyListFilters = {
    cliente: null,
    vendedor: null,
    noPoliza: '',
    concepto: '',
    abreviacion: '',
    anio: currentYear,
    conducto: '',
    ramo: '',
    esRobot: false,
}

export const NEW_POLICY_LOCATION: { pathname: string; state: PolicyFormLocationState } = {
    pathname: NEW_POLICY_PATH,
    state: { backUrl: POLICIES_PATH },
}

const buildQuery = (filters: PolicyListFilters) => {
    const params: string[] = []
    if (filters.cliente?.id) params.push(`clienteId=${filters.cliente.id}`)
    if (filters.vendedor?.id) params.push(`vendedorId=${filters.vendedor.id}`)
    if (filters.noPoliza.trim()) params.push(`noPoliza[cn]=${encodeURIComponent(filters.noPoliza.trim())}`)
    if (filters.concepto.trim()) params.push(`concepto[cn]=${encodeURIComponent(filters.concepto.trim())}`)
    if (filters.abreviacion.trim()) params.push(`abreviacion[cn]=${encodeURIComponent(filters.abreviacion.trim())}`)
    if (filters.anio) params.push(`fechaVigenciaDe[bw]=${filters.anio}-01-01,${filters.anio}-12-31`)
    if (filters.conducto) params.push(`conducto=${encodeURIComponent(filters.conducto)}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    if (filters.esRobot) params.push('esRobot=true')
    return params.join('&')
}

const toRow = (item: SearchPolizaDTO): PolicyRow => ({
    id: item.polizaId,
    noPoliza: item.noPoliza,
    ramo: item.ramo,
    esRobot: Boolean(item.esRobot),
    name: personName(item),
    seller: personName({ nombres: item.vendedorNombres, apPat: item.vendedorApPat, apMat: item.vendedorApMat }),
    concepto: item.concepto,
    aseguradora: item.aseguradora,
    agenteClave: item.agenteClave,
    total: item.total,
    moneda: item.moneda,
    status: statusView(item.status, item.fechaVigenciaDe, item.fechaVigenciaA),
    vigencia: vigenciaView(item.fechaVigenciaDe, item.fechaVigenciaA, item.status),
})

export const usePolicyListModel = () => {
    const history = useHistory()
    const filtersRef = useRef<PolicyListFilters>(defaultFilters)
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<PolicyListFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters))
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchPolizaDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(null)
    const [conductos, setConductos] = useState<KeyValue[]>([])

    useEffect(() => {
        let active = true
        const loadConductos = async () => {
            try {
                const response = await APIInvoke.invokeGET('/catalogos/metodo-pago')
                const payload: WrapperResponse<KeyValue[]> | null = await response.json()
                if (active && payload?.ok) setConductos(payload.body || [])
            } catch {
                if (active) setConductos([])
            }
        }
        loadConductos()
        return () => { active = false }
    }, [])

    useEffect(() => () => clearTimeout(textTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const extra = query ? `&${query}` : ''
                const response = await APIInvoke.invokeGET(`/polizas?page=${page}&size=${pageSize}${extra}`)
                const payload: WrapperResponse<PagaebleResult<SearchPolizaDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, query, reloadToken])

    const commit = (next: PolicyListFilters) => {
        clearTimeout(textTimer.current)
        filtersRef.current = next
        setFilters(next)
        setPage(1)
        setQuery(buildQuery(next))
    }

    const commitPatch = (partial: Partial<PolicyListFilters>) => commit({ ...filtersRef.current, ...partial })

    const changeText = (key: PolicyTextFilter, value: string) => {
        const next = { ...filtersRef.current, [key]: value }
        filtersRef.current = next
        setFilters(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => {
            setPage(1)
            setQuery(buildQuery(filtersRef.current))
        }, TEXT_DELAY_MS)
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters,
        conductos,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        detailId,
        changeCliente: (cliente: ClienteDTO | null) => commitPatch({ cliente }),
        changeVendedor: (vendedor: SearchVendedorDTO | null) => commitPatch({ vendedor }),
        changeText,
        submitText: () => commit(filtersRef.current),
        changeAnio: (value: string) => commitPatch({ anio: value === '' ? '' : Number(value) }),
        changeConducto: (value: string) => commitPatch({ conducto: value as Conducto | '' }),
        changeRamo: (ramo: string) => commitPatch({ ramo }),
        changeEsRobot: (esRobot: boolean) => commitPatch({ esRobot }),
        clearFilters: () => commit({ ...defaultFilters }),
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        openPoliza: (id: number) => setDetailId(id),
        closePoliza: () => setDetailId(null),
        viewPoliza: (id: number) => history.push(policyPath(id)),
        editPoliza: (id: number) => {
            const state: PolicyFormLocationState = { backUrl: POLICIES_PATH }
            history.push(`${policyPath(id)}/edit`, state)
        },
    }
}
